import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText, Loader2 } from "lucide-react";
import { useAdminSearch } from "@/hooks/useDocSearch";
import AlgoliaHighlight from "@/components/AlgoliaHighlight";

export default function AdminGlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { results, loading } = useAdminSearch({ query, enabled: open });

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (hit) => {
    setOpen(false);
    setQuery("");
    navigate(`/spaces/${hit.space_id}/articles/${hit.id}`);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="admin-global-search-trigger"
        className="hidden lg:flex items-center gap-2 px-3 py-2 text-sm text-zinc-500 bg-white border border-zinc-200 rounded-sm hover:border-zinc-300 min-w-[220px]"
      >
        <Search className="w-4 h-4" />
        <span className="flex-1 text-left">Search all spaces…</span>
        <kbd className="text-[10px] bg-zinc-100 px-1.5 py-0.5 rounded border border-zinc-200">⌘K</kbd>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4 bg-black/40" onClick={() => setOpen(false)}>
      <div
        className="w-full max-w-xl bg-white rounded-sm shadow-2xl border border-zinc-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-global-search-modal"
      >
        <div className="flex items-center gap-2 px-4 border-b border-zinc-200">
          <Search className="w-4 h-4 text-zinc-400 shrink-0" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles across all spaces…"
            className="flex-1 py-3 text-sm outline-none"
            data-testid="admin-global-search-input"
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />}
        </div>
        <div className="max-h-[50vh] overflow-y-auto">
          {query.trim().length < 2 ? (
            <div className="px-4 py-8 text-center text-sm text-zinc-500">Type at least 2 characters…</div>
          ) : results.length === 0 && !loading ? (
            <div className="px-4 py-8 text-center text-sm text-zinc-500">No results for &quot;{query}&quot;</div>
          ) : (
            results.map((r) => (
              <button
                key={r.objectID || r.id}
                type="button"
                onClick={() => go(r)}
                className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-zinc-50 border-b border-zinc-100 last:border-0"
              >
                <FileText className="w-4 h-4 text-[#41B5E6] mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-zinc-900 truncate">
                    <AlgoliaHighlight html={r._highlight?.title} text={r.title} query={query} />
                  </div>
                  <div className="text-xs text-zinc-500 truncate mt-0.5">
                    {r.space_name} · {r.status}
                    {r.lang === "ar" ? " · AR" : " · EN"}
                  </div>
                  {r._highlight?.excerpt && (
                    <div className="text-xs text-zinc-400 line-clamp-1 mt-1">
                      <AlgoliaHighlight html={r._highlight.excerpt} text={r.excerpt} query={query} />
                    </div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
